import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { RefreshCcw, Save, X } from 'lucide-react';
import { Switch } from '../../ui/switch';
import { toast } from '../../../hooks/use-toast';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';

interface BrandingCustomizationProps {
  onSave: (branding: {
    logo: string;
    header: string;
    primaryColor: string;
    secondaryColor: string;
    isOn: boolean;
    logoFile: File | null;
    headerFile: File | null;
  }) => void;
  existing: {
    logo: string;
    header: string;
    primaryColor: string;
    secondaryColor: string;
    isOn: boolean;
  };
  selectedCompanyId: string;
}

const DEFAULT_LOGO = 'https://sparqle-production.s3.eu-central-1.amazonaws.com/SparqleLogo.png';
const DEFAULT_HEADER = 'https://sparqle-production.s3.eu-central-1.amazonaws.com/TrackingHeader2.png';
const DEFAULT_PRIMARY_COLOR = '#93BFFF';
const DEFAULT_SECONDARY_COLOR = '#f5fcfd';

// Aspect ratio for the header image (16:9)
const HEADER_ASPECT_RATIO = 600 / 220;

export function BrandingCustomization({ onSave, existing, selectedCompanyId }: BrandingCustomizationProps) {
  const [isOn, setIsOn] = useState<boolean>(false);
  const [logo, setLogo] = useState<string>('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [header, setHeader] = useState<string>('');
  const [headerFile, setHeaderFile] = useState<File | null>(null);
  const [primaryColor, setPrimaryColor] = useState<string>(DEFAULT_PRIMARY_COLOR);
  const [secondaryColor, setSecondaryColor] = useState<string>(DEFAULT_SECONDARY_COLOR);
  const [primaryColorError, setPrimaryColorError] = useState<boolean>(false);
  const [secondaryColorError, setSecondaryColorError] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  // Cropping state
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [tempImage, setTempImage] = useState<string>('');
  const [_, setTempFile] = useState<File | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // 2. Update the onCropComplete function to store the crop area
  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Add refs for the file inputs
  const logoInputRef = useRef<HTMLInputElement>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectedCompanyId) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    if (existing) {
      setIsOn(existing.isOn);
      setLogo(existing.logo);
      setHeader(existing.header);
      setPrimaryColor(existing.primaryColor);
      setSecondaryColor(existing.secondaryColor);
    }
  }, [existing]);

  const isValidColor = (color: string): boolean => {
    const s = new Option().style;
    s.color = color;
    return s.color !== '';
  };

  const handlePrimaryColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setPrimaryColor(newColor);
    setPrimaryColorError(!isValidColor(newColor));
  };

  const handleSecondaryColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setSecondaryColor(newColor);
    setSecondaryColorError(!isValidColor(newColor));
  };

  const handleSave = () => {
    if (!primaryColorError && !secondaryColorError) {
      onSave({
        logo: logoFile ? URL.createObjectURL(logoFile) : logo,
        header: headerFile ? URL.createObjectURL(headerFile) : header,
        primaryColor,
        secondaryColor,
        isOn,
        logoFile,
        headerFile,
      });
    }
  };

  const handleReset = () => {
    setLogo('');
    setHeader('');
    setPrimaryColor(DEFAULT_PRIMARY_COLOR);
    setSecondaryColor(DEFAULT_SECONDARY_COLOR);
    setPrimaryColorError(false);
    setSecondaryColorError(false);
    setLogoFile(null);
    setHeaderFile(null);
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
    if (headerInputRef.current) {
      headerInputRef.current.value = '';
    }
  };

  const clearLogo = () => {
    setLogo('');
    setLogoFile(null);
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const clearHeader = () => {
    setHeader('');
    setHeaderFile(null);
    if (headerInputRef.current) {
      headerInputRef.current.value = '';
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const isLt5M = file.size / 1024 / 1024 <= 5;
      if (!isLt5M) {
        toast({
          title: 'Error',
          description: 'Image must be smaller than 5MB!',
          variant: 'destructive',
        });
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleHeaderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const isLt5M = file.size / 1024 / 1024 <= 5;
      if (!isLt5M) {
        toast({
          title: 'Error',
          description: 'Image must be smaller than 5MB!',
          variant: 'destructive',
        });
        return;
      }
      setTempFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImage(reader.result as string);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const getCroppedImg = (src: string, pixelCrop: any): Promise<File> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = src;
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('No 2d context'));
          return;
        }

        // Set canvas size to match the crop area
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        // Draw the cropped image
        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height,
        );

        // Convert canvas to blob and then to File
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas is empty'));
              return;
            }

            const file = new File([blob], 'cropped-header.jpg', {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(file);
          },
          'image/jpeg',
          0.95,
        );
      };

      image.onerror = () => reject(new Error('Failed to load image'));
    });
  };

  // 4. Update the crop and save handler
  const handleCropAndSave = async () => {
    if (!tempImage || !croppedAreaPixels) {
      toast({
        title: 'Error',
        description: 'No crop area selected',
        variant: 'destructive',
      });
      return;
    }

    try {
      const croppedFile = await getCroppedImg(tempImage, croppedAreaPixels);

      // Create a URL for preview
      const croppedImageUrl = URL.createObjectURL(croppedFile);

      setHeader(croppedImageUrl);
      setHeaderFile(croppedFile);
      setShowCropper(false);
      setTempImage('');
      setTempFile(null);
      setCroppedAreaPixels(null);

      toast({
        title: 'Success',
        description: 'Header image cropped successfully',
      });
    } catch (e) {
      console.error(e);
      toast({
        title: 'Error',
        description: 'Failed to crop image',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="grid grid-cols-2 gap-8">
      {/* Left side - Controls */}
      <div className="space-y-6 sticky top-24 self-start">
        <Card>
          <CardHeader>
            <CardTitle>Branding Customization</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Enable Custom Branding</Label>
                  <p className="text-sm text-muted-foreground">Enable to use custom branding for track & trace</p>
                </div>
                <Switch
                  checked={isOn}
                  onCheckedChange={(checked) => {
                    setIsOn(checked);
                  }}
                />
              </div>
            </div>
            {/* Logo Upload */}
            <div className="space-y-2">
              <Label>Company Logo</Label>
              <div className="flex items-center gap-2">
                <Input type="file" accept="image/*" ref={logoInputRef} onChange={handleLogoChange} className="flex-1" />
                {logo && (
                  <div className="flex items-center gap-1">
                    <img src={logo} alt="Logo preview" className="h-8 w-8 object-contain" />
                    <Button variant="ghost" size="icon" onClick={clearLogo} className="h-8 w-8">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Header Image Upload */}
            <div className="space-y-2">
              <Label>Header Image</Label>
              <div className="flex items-center gap-2">
                <Input
                  ref={headerInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleHeaderChange}
                  className="flex-1"
                />
                {header && (
                  <div className="flex items-center gap-1">
                    <img src={header} alt="Header preview" className="h-8 w-8 object-contain" />
                    <Button variant="ghost" size="icon" onClick={clearHeader} className="h-8 w-8">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Color Pickers */}
            <div className="space-y-2">
              <Label>Primary Color</Label>
              <div className="flex items-center gap-2">
                <Input type="color" value={primaryColor} onChange={handlePrimaryColorChange} className="w-12 h-8 p-1" />
                <Input
                  type="text"
                  value={primaryColor}
                  onChange={handlePrimaryColorChange}
                  className={`flex-1 ${primaryColorError ? 'border-red-500' : ''}`}
                />
              </div>
              {primaryColorError && <p className="text-sm text-red-500">Please enter a valid color</p>}
            </div>

            <div className="space-y-2">
              <Label>Secondary Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={secondaryColor}
                  onChange={handleSecondaryColorChange}
                  className="w-12 h-8 p-1"
                />
                <Input
                  type="text"
                  value={secondaryColor}
                  onChange={handleSecondaryColorChange}
                  className={`flex-1 ${secondaryColorError ? 'border-red-500' : ''}`}
                />
              </div>
              {secondaryColorError && <p className="text-sm text-red-500">Please enter a valid color</p>}
            </div>
            <div className="flex justify-end gap-4">
              <Button className="px-2" variant="outline" onClick={handleReset} disabled={isDisabled}>
                <RefreshCcw />
              </Button>
              {/* <Button className="px-8" variant="outline" disabled={isDisabled}>
                <Mail />
                Send Test E-mail
              </Button> */}
              <Button
                className="px-8"
                onClick={handleSave}
                disabled={primaryColorError || secondaryColorError || isDisabled}
              >
                <Save />
                Save
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right side - Preview */}
      <div className="space-y-6">
        <Card>
          <CardContent className="p-0 m-0">
            <div className="p-5 rounded-lg bg-[#f5fcfd]" style={{ backgroundColor: secondaryColor }}>
              <div className="max-w-[600px] mx-auto bg-white rounded-xl overflow-visible relative">
                {/* Email Header */}
                <div className="h-[220px] overflow-hidden rounded-t-xl relative p-0 mb-0 bg-white text-center">
                  <img
                    src={header || DEFAULT_HEADER}
                    className="w-full h-full object-cover object-top block rounded-t-xl"
                    alt="Header"
                  />
                </div>

                {/* Profile Logo */}
                <div className="text-center w-full">
                  <img
                    src={logo || DEFAULT_LOGO}
                    alt="Company Logo"
                    className="block w-20 h-20 rounded-xl border-2 border-white bg-white shadow-md mx-auto -mt-11 relative z-10"
                    style={{ backgroundColor: secondaryColor }}
                  />
                </div>

                {/* Email Content */}
                <div className="px-10 py-10 pb-8 text-left text-base leading-relaxed">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">Your Order is on its way!</h2>
                  <p className="mb-4">Hi John,</p>
                  <p className="mb-4">
                    We are excited to let you know that your order #12345 is now in transit and on its way to you.
                  </p>
                  <div className="text-center mb-4">
                    <a
                      href="#"
                      className="inline-block mt-4 px-5 py-2.5 rounded text-white font-bold no-underline"
                      style={{ backgroundColor: primaryColor, color: '#ffffff' }}
                    >
                      Track My Shipment
                    </a>
                  </div>
                  <p className="mb-4">
                    Thanks,
                    <br />
                    Sparqle
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center text-xs text-gray-500 mt-1">
                <p>&copy; 2025 Sparqle Technologies. All rights reserved.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Image Cropper Modal */}
      <Dialog open={showCropper} onOpenChange={setShowCropper}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Crop Header Image</DialogTitle>
          </DialogHeader>
          <div className="relative h-[400px] w-full">
            <Cropper
              image={tempImage}
              crop={crop}
              zoom={zoom}
              aspect={HEADER_ASPECT_RATIO}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <div className="flex justify-end gap-4 mt-4">
            <Button variant="outline" onClick={() => setShowCropper(false)}>
              Cancel
            </Button>
            <Button onClick={handleCropAndSave}>Crop & Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
